/**
 * useMessageSync - 消息同步器
 *
 * 职责：
 * 1. 将服务端数据适配并同步到 Store
 * 2. 创建选择回调（Choice Actions）
 * 3. 管理消息追加逻辑
 *
 * 注意：该 Hook 内部调用 useEnginePlay 获取 makeChoiceAsync
 */
import { useCallback, useMemo } from 'react'
import { logger } from '@/lib/logger'
import {
  adaptServerMessagesToUI,
  adaptSceneToUIMessage,
  createUserMessage,
  type BatchAdapterInput,
  type ServerCurrentScene,
} from '../lib/adapters'
import { useEngineStore } from '../stores/use-engine-store'
import { useEnginePlay } from './use-engine-play'
import { useGalgameController } from './use-galgame-controller'

export const useMessageSync = (sessionId: string | undefined) => {
  // 基础依赖
  const setMessages = useEngineStore((s) => s.messages.setMessages)
  const { initSegments } = useGalgameController()

  // 获取 API 函数
  const { makeChoiceAsync } = useEnginePlay(sessionId)

  /**
   * 内部辅助函数：追加新场景
   * 提前声明，供 createChoiceAction 引用，避免变量在声明前被访问
   */
  const appendSceneToStoreInternal = useCallback(
    (
      scene: ServerCurrentScene,
      currentSessionId: string,
      choiceActionCreator: (
        sid: string
      ) => (choice: { id: string; text: string }) => () => Promise<void>
    ) => {
      const currentMessages = useEngineStore.getState().messages.messages || []
      const newMessage = adaptSceneToUIMessage(
        scene,
        choiceActionCreator(currentSessionId)
      )

      logger.debug('[appendSceneToStore] 追加前消息数:', currentMessages.length)
      setMessages([...currentMessages, newMessage])
      logger.debug(
        '[appendSceneToStore] 追加后消息数:',
        currentMessages.length + 1
      )
      initSegments()
    },
    [setMessages, initSegments]
  )

  /**
   * 创建选择回调
   */
  const createChoiceAction = useCallback(
    (currentSessionId: string) =>
      (choice: { id: string; text: string }) =>
      async () => {
        const currentMessages =
          useEngineStore.getState().messages.messages || []
        setMessages([...currentMessages, createUserMessage(choice.text)])

        const newScene = await makeChoiceAsync({
          sessionId: currentSessionId,
          data: { choiceId: choice.id },
        })

        if (newScene) {
          // eslint-disable-next-line react-hooks/immutability -- createChoiceAction 在异步回调中递归引用自身以生成下一轮选择回调，属有意自引用
          const choiceActionCreator = (sid: string) => createChoiceAction(sid)
          appendSceneToStoreInternal(
            newScene as ServerCurrentScene,
            currentSessionId,
            choiceActionCreator
          )
        }
      },
    [makeChoiceAsync, setMessages, appendSceneToStoreInternal]
  )

  /**
   * 同步历史消息
   */
  const syncMessagesToStore = useCallback(
    (input: BatchAdapterInput, currentSessionId: string) => {
      if (!currentSessionId) return

      const uiMessages = adaptServerMessagesToUI(
        input,
        createChoiceAction(currentSessionId)
      )

      setMessages(uiMessages)
      initSegments()
    },
    [setMessages, initSegments, createChoiceAction]
  )

  /**
   * 追加场景（用于轮询结果）
   */
  const syncAppendSceneToStore = useCallback(
    (scene: ServerCurrentScene, currentSessionId: string) => {
      const choiceActionCreator = (sid: string) => createChoiceAction(sid)
      appendSceneToStoreInternal(scene, currentSessionId, choiceActionCreator)
    },
    [createChoiceAction, appendSceneToStoreInternal]
  )

  /**
   * 追加用户自定义消息
   */
  const appendUserMessage = useCallback(
    (text: string) => {
      const currentMessages = useEngineStore.getState().messages.messages || []
      setMessages([...currentMessages, createUserMessage(text)])
    },
    [setMessages]
  )

  return useMemo(
    () => ({
      syncMessagesToStore,
      syncAppendSceneToStore,
      appendUserMessage,
      createChoiceAction,
    }),
    [
      syncMessagesToStore,
      syncAppendSceneToStore,
      appendUserMessage,
      createChoiceAction,
    ]
  )
}
